<template>
  <aside class="app-sidebar" :class="{ 'sidebar-open': sidebarOpen }">
    <!-- Header: Logo -->
    <div class="sidebar-header">
      <RouterLink to="/">
        <img src="/logo-mini.svg" alt="ForMind" class="sidebar-logo-mini" />
      </RouterLink>
      <!-- Mobile close button -->
      <button
        v-if="isMobile && sidebarOpen"
        class="sidebar-close-btn"
        :aria-label="t('common.close')"
        @click="closeSidebar"
      >
        <X :size="20" />
      </button>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav">
      <!-- Yeni Sohbet -->
      <button
        v-if="authStore.isClient || authStore.isAdmin || authStore.isTest"
        class="nav-item"
        :class="{ active: isRoute('chat') && !route.query.conversationId }"
        @click="handleNewChat"
      >
        <Plus :size="20" />
        <span class="sidebar-tooltip">Yeni Sohbet</span>
        <span class="nav-label">Yeni Sohbet</span>
      </button>

      <!-- Arama -->
      <RouterLink
        v-if="authStore.isClient || authStore.isAdmin"
        to="/search"
        class="nav-item"
        :class="{ active: isRoute('search') }"
        @click="closeSidebar"
      >
        <Search :size="20" />
        <span class="sidebar-tooltip">Arama</span>
        <span class="nav-label">Arama</span>
      </RouterLink>

      <!-- Agent / Araçlar (admin only) -->
      <RouterLink
        v-if="authStore.isAdmin"
        to="/workflow"
        class="nav-item"
        :class="{ active: isRoute('workflow') }"
        @click="closeSidebar"
      >
        <Network :size="20" />
        <span class="sidebar-tooltip">Workflow Agents</span>
        <span class="nav-label">Workflow Agents</span>
      </RouterLink>

      <!-- Admin-only items -->
      <RouterLink
        v-if="authStore.isAdmin"
        to="/users"
        class="nav-item"
        :class="{ active: isRoute('users') }"
        @click="closeSidebar"
      >
        <Users :size="20" />
        <span class="sidebar-tooltip">{{ t('sidebar.userManagement') }}</span>
        <span class="nav-label">{{ t('sidebar.userManagement') }}</span>
      </RouterLink>

      <RouterLink
        v-if="authStore.isAdmin"
        to="/models"
        class="nav-item"
        :class="{ active: isRoute('models') }"
        @click="closeSidebar"
      >
        <Lightbulb :size="20" />
        <span class="sidebar-tooltip">Model Yönetimi</span>
        <span class="nav-label">Model Yönetimi</span>
      </RouterLink>

      <RouterLink
        v-if="authStore.isAdmin"
        to="/skills"
        class="nav-item"
        :class="{ active: isRoute('skills') }"
        @click="closeSidebar"
      >
        <Zap :size="20" />
        <span class="sidebar-tooltip">Skill Yönetimi</span>
        <span class="nav-label">Skill Yönetimi</span>
      </RouterLink>

      <RouterLink
        v-if="authStore.isAdmin"
        to="/prepared-questions"
        class="nav-item"
        :class="{ active: isRoute('prepared-questions') }"
        @click="closeSidebar"
      >
        <ListChecks :size="20" />
        <span class="sidebar-tooltip">Hazır Sorular</span>
        <span class="nav-label">Hazır Sorular</span>
      </RouterLink>
      <RouterLink
        v-if="authStore.isAdmin"
        to="/rag-documents"
        class="nav-item"
        :class="{ active: isRoute('rag-documents') }"
        @click="closeSidebar"
      >
        <Database :size="20" />
        <span class="sidebar-tooltip">RAG Dokümanları</span>
        <span class="nav-label">RAG Dokümanları</span>
      </RouterLink>

      <RouterLink
        v-if="authStore.isAdmin"
        to="/widget-renderer"
        class="nav-item"
        :class="{ active: isRoute('widget-renderer') }"
        @click="closeSidebar"
      >
        <PanelsTopLeft :size="20" />
        <span class="sidebar-tooltip">Widget Testi</span>
        <span class="nav-label">Widget Testi</span>
      </RouterLink>

      <RouterLink
        to="/my-documents"
        class="nav-item"
        :class="{ active: isRoute('my-documents') }"
        @click="closeSidebar"
      >
        <FileText :size="20" />
        <span class="sidebar-tooltip">Dokümanlarım</span>
        <span class="nav-label">Dokümanlarım</span>
      </RouterLink>

      <RouterLink
        v-if="authStore.isAdmin"
        to="/observability/traces"
        class="nav-item"
        :class="{ active: isRoute('observability') }"
        @click="closeSidebar"
      >
        <GitBranch :size="20" />
        <span class="sidebar-tooltip">Observability</span>
        <span class="nav-label">Observability</span>
      </RouterLink>
      <RouterLink v-if="authStore.isAdmin" to="/event-testing" class="nav-item"
        :class="{ active: isRoute('event-testing') }" @click="closeSidebar">
        <Radio :size="20" />
        <span class="sidebar-tooltip">Event Testing</span>
        <span class="nav-label">Event Testing</span>
      </RouterLink>
    </nav>

    <!-- Footer -->
    <div class="sidebar-footer">

      <!-- Portfolio Panel -->
      <PortfolioPanel />

      <!-- Settings -->
      <RouterLink
        to="/profile"
        class="nav-item"
        :class="{ active: isRoute('profile') }"
        @click="closeSidebar"
      >
        <Settings :size="20" />
        <span class="sidebar-tooltip">Ayarlar</span>
        <span class="nav-label">Ayarlar</span>
      </RouterLink>

      <!-- Portföy Login -->
      <button
        class="nav-item trader-mcp-btn"
        :class="{ 'trader-connected': traderMcpStore.isConnected, 'trader-connecting': traderMcpStore.isConnecting }"
        @click="handleTraderClick"
      >
        <Briefcase :size="20" />
        <span class="sidebar-tooltip">{{ traderTooltip }}</span>
        <span class="nav-label">{{ traderTooltip }}</span>
      </button>

      <!-- WebSocket connection status dot -->
      <div class="connection-status-wrapper" :aria-label="connectionTooltipText">
        <span class="connection-dot" :class="connectionClass"></span>
        <span class="sidebar-tooltip">{{ connectionTooltipText }}</span>
      </div>

      <!-- Token usage mini progress bar -->
      <div class="usage-progress-wrapper" :aria-label="`Token: ${usagePercent}%`">
        <div class="usage-progress-bar">
          <div class="usage-progress-fill" :style="{ width: usagePercent + '%' }"></div>
        </div>
        <span class="sidebar-tooltip">Token: {{ usagePercent }}%</span>
      </div>

      <!-- Profile avatar -->
      <RouterLink to="/profile" class="profile-nav-item" @click="closeSidebar">
        <span class="profile-avatar">{{ userInitial }}</span>
        <span class="sidebar-tooltip">Profil</span>
        <span class="nav-label">Profil</span>
      </RouterLink>

      <!-- Logout -->
      <button class="logout-item" @click="handleLogout">
        <LogOut :size="20" />
        <span class="sidebar-tooltip">Çıkış</span>
        <span class="nav-label">Çıkış</span>
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useConnectionStore } from '@/stores/connection'
import { useTraderMcpStore } from '@/stores/traderMcp'
import { useSidebar } from '@/composables/useSidebar'
import { useResponsive } from '@/composables/useResponsive'
import {
  X, Plus, Search, Network, Users, Lightbulb,
  GitBranch, Radio, Settings, LogOut, Zap, Briefcase, Database, FileText, PanelsTopLeft, ListChecks
} from 'lucide-vue-next'
import PortfolioPanel from '@/components/PortfolioPanel.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const connectionStore = useConnectionStore()
const traderMcpStore = useTraderMcpStore()
const { sidebarOpen, closeSidebar } = useSidebar()
const { isMobile } = useResponsive()

// Active route detection
function isRoute(name: string): boolean {
  const path = route.path
  if (name === 'chat') return path === '/chat'
  if (name === 'search') return path === '/search'
  if (name === 'workflow') return path === '/workflow'
  if (name === 'users') return path === '/users'
  if (name === 'models') return path === '/models'
  if (name === 'skills') return path === '/skills'
  if (name === 'prepared-questions') return path === '/prepared-questions'
  if (name === 'rag-documents') return path === '/rag-documents'
  if (name === 'widget-renderer') return path === '/widget-renderer'
  if (name === 'my-documents') return path === '/my-documents'
  if (name === 'observability') return path.startsWith('/observability')
  if (name === 'profile') return path === '/profile'
  return false
}

// Connection status from shared store
const connectionClass = computed(() => connectionStore.connectionClass)
const connectionTooltipText = computed(() => connectionStore.connectionTooltip)

// Token usage from shared store
const usagePercent = computed(() => connectionStore.usagePercent)

// User initial for profile avatar
const userInitial = computed(() => {
  const username = authStore.authInfo?.user?.username
  if (username && username.length > 0) {
    return username.charAt(0).toUpperCase()
  }
  return '?'
})

// New chat: navigate to /chat without conversationId (force new conversation)
function handleNewChat() {
  closeSidebar()
  if (route.name === 'chat' && !route.query.conversationId) {
    window.dispatchEvent(new CustomEvent('formind:new-chat'))
  } else {
    void router.push({ path: '/chat', query: {} })
  }
}

// Trader MCP tooltip
const traderTooltip = computed(() => {
  if (traderMcpStore.isConnected) return 'Portföy Bağlı'
  if (traderMcpStore.isConnecting) return 'Bağlanıyor...'
  return 'Portföy Girişi'
})

// Trader MCP button click: if connected → show portfolio, else → start OAuth popup
function handleTraderClick() {
  if (traderMcpStore.isConnected) {
    traderMcpStore.openPortfolio()
  } else {
    traderMcpStore.startOAuthLogin()
  }
}

async function handleLogout() {
  traderMcpStore.reset()
  await authStore.logout()
  router.push('/')
}

// Check trader MCP status on mount
onMounted(() => {
  traderMcpStore.checkStatus()
})
</script>

<style scoped src="@/assets/styles/components/AppSidebar.css"></style>
